import countries from 'i18n-iso-countries';
import moment from 'moment';
import { useMemo } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import { TypedMap } from 'flow/types/TypedMap';
import { ContactFull } from 'flow/types/Contact';
import { TelephoneNumber } from 'flow/types/TelephoneNumber';
import {
    makeSelectExtensionKeyFromContactKey,
    selectUserCountryCode,
} from 'flow/selectors/ExtensionSelectors';
import NumberWrapper from 'flow/components/ui-components/NumberWrapper';

interface Props {
    contact?: TypedMap<ContactFull>;
    contactFullName?: string;
    number?: TypedMap<TelephoneNumber>;
    inline?: boolean;
}

const getSecondaryText = ({
    contactFullName,
    companyName,
    department,
    contactIsAnExtension,
}: {
    contactFullName?: string;
    companyName?: string;
    department?: string;
    contactIsAnExtension: boolean;
}) => {
    // company is the title, don't repeat it
    if (!contactFullName) return undefined;
    if (contactIsAnExtension && department) return department;
    return companyName;
};

const CountryFallback = ({
    number,
    inline,
}: {
    number?: TypedMap<TelephoneNumber>;
    inline?: boolean;
}) => {
    const countryCode =
        number?.get('type') === 'e164' ? number?.get('countryCode') : undefined;
    const country = countries.getName(countryCode, moment.locale());
    if (!country) return null;
    return <>{inline ? `(${country})` : country}</>;
};

const ContactSubtitle = ({
    contact,
    contactFullName,
    number,
    inline,
}: Props) => {
    const userCountryCode = useAppSelector(selectUserCountryCode);
    const contactKey = contact?.get('key');
    const selectExtensionKey = useMemo(
        makeSelectExtensionKeyFromContactKey,
        [],
    );
    const contactIsAnExtension = useAppSelector(
        (state) => !!selectExtensionKey(state, contactKey),
    );

    if (number?.get('type') === 'technical_number') {
        return null;
    }

    const companyName = contact?.get('companyName');

    // No name and no company — title shows the number, subtitle shows country
    if (!contactFullName && !companyName) {
        return <CountryFallback number={number} inline={inline} />;
    }

    // Name or company exists — subtitle shows number + optional secondary
    const secondaryText = getSecondaryText({
        contactFullName,
        companyName,
        department: contact?.get('department'),
        contactIsAnExtension,
    });

    const formattedNumber = prettyFormatE164Number(
        number?.get('number'),
        userCountryCode,
    );

    if (!formattedNumber && !secondaryText) return null;

    const content = (
        <>
            {formattedNumber && (
                <NumberWrapper>{formattedNumber}</NumberWrapper>
            )}
            {secondaryText && (
                <span>
                    {formattedNumber ? ` • ${secondaryText}` : secondaryText}
                </span>
            )}
        </>
    );

    if (!inline) return content;

    return (
        <>
            {'('}
            {content}
            {')'}
        </>
    );
};

export default ContactSubtitle;
